export function withRebuildCompanySearchConditions(destination, search = window.location.search) {
  const url = new URL(destination, window.location.origin)
  new URLSearchParams(search).forEach((value, key) => {
    if (/^rebuild_(methods|strengths)\[\d*\]$/.test(key)) {
      url.searchParams.append(key, value)
    }
  })
  return `${url.pathname}${url.search}${url.hash}`
}

export function initRebuildCompanyArchiveAreaSearchToggle(root = document) {
  const toggles = root.querySelectorAll('[data-rebuild-company-area-search-toggle]')

  toggles.forEach((toggle) => {
    if (toggle.dataset.rebuildCompanyAreaSearchToggleInitialized === 'true') {
      return
    }

    const button = toggle.querySelector('[data-rebuild-company-area-search-button]')
    const panel = toggle.querySelector('[data-rebuild-company-area-search-panel]')
    const target = toggle.querySelector('[data-rebuild-company-area-search-target]')
    const closeButton = toggle.querySelector('[data-rebuild-company-area-search-close]')

    if (!button || !panel || !target) {
      return
    }

    toggle.dataset.rebuildCompanyAreaSearchToggleInitialized = 'true'

    const loadAreaSearchForm = async () => {
      const url = target.dataset.rebuildCompanyAreaSearchUrl || ''

      if (!url || target.dataset.rebuildCompanyAreaSearchLoaded === 'true' || target.dataset.rebuildCompanyAreaSearchLoading === 'true') {
        return
      }

      target.dataset.rebuildCompanyAreaSearchLoading = 'true'

      try {
        const response = await fetch(url)
        if (!response.ok) throw new Error(`Failed to load area search form: ${response.status}`)

        target.innerHTML = await response.text()
        target.dataset.rebuildCompanyAreaSearchLoaded = 'true'
        initRebuildCompanyArchiveSearchForm(target)
      } catch (error) {
        target.innerHTML = '<div class="w-full rounded-xl bg-white px-2.5 py-2 text-center text-[13px] font-bold text-hapisumu-rebuild">エリア変更フォームを読み込めませんでした。</div>'
      } finally {
        delete target.dataset.rebuildCompanyAreaSearchLoading
      }
    }

    button.addEventListener('click', () => {
      button.hidden = true
      panel.hidden = false
      loadAreaSearchForm()
    })

    closeButton?.addEventListener('click', () => {
      panel.hidden = true
      button.hidden = false
    })
  })
}

export function initRebuildCompanyArchiveSearchForm(root = document, navigate = (url) => window.location.assign(url)) {
  const forms = root.querySelectorAll('.js-rebuild-company-search-form')

  forms.forEach((form) => {
    if (form.dataset.rebuildCompanyArchiveSearchFormInitialized === 'true') {
      return
    }

    const prefectureSelect = form.querySelector('.js-rebuild-company-search-prefecture')
    const citySelect = form.querySelector('.js-rebuild-company-search-city')
    const cityName = form.querySelector('.js-rebuild-company-search-city-name')
    const cityCount = form.querySelector('.js-rebuild-company-search-city-count')

    if (!prefectureSelect || !citySelect) {
      return
    }

    form.dataset.rebuildCompanyArchiveSearchFormInitialized = 'true'
    const cityOptionsUrl = form.dataset.cityOptionsUrl || ''

    const syncCityDisplay = () => {
      if (!cityName || !cityCount) {
        return
      }

      const selectedCity = citySelect.selectedOptions[0]
      cityName.textContent = selectedCity ? (selectedCity.dataset.cityName || selectedCity.textContent.trim()) : '市区町村を選択'
      cityCount.textContent = selectedCity ? (selectedCity.dataset.countLabel || '') : ''
    }

    const cityPlaceholderOption = () => {
      const option = document.createElement('option')
      option.value = ''
      option.dataset.cityName = '市区町村を選択'
      option.textContent = '市区町村を選択'
      return option
    }

    const syncCityOptions = () => {
      const selectedPrefecture = prefectureSelect.selectedOptions[0]
      const selectedPrefectureSlug = selectedPrefecture ? selectedPrefecture.dataset.prefectureSlug : ''
      let selectableCityCount = 0

      Array.from(citySelect.options).forEach((option) => {
        if (!option.value) {
          option.hidden = false
          option.disabled = false
          return
        }

        const matchesPrefecture = selectedPrefectureSlug !== '' && option.dataset.prefectureSlug === selectedPrefectureSlug
        const hasCompanies = option.dataset.hasCompanies !== '0'
        option.hidden = !matchesPrefecture
        option.disabled = !matchesPrefecture || !hasCompanies

        if (matchesPrefecture && hasCompanies) {
          selectableCityCount += 1
        }
      })

      const selectedCity = citySelect.selectedOptions[0]
      if (selectedCity && (selectedCity.hidden || selectedCity.disabled)) {
        citySelect.value = ''
      }

      citySelect.disabled = selectedPrefectureSlug === '' || selectableCityCount === 0
      syncCityDisplay()
    }

    const replaceCityOptionsWithPlaceholder = () => {
      citySelect.replaceChildren(cityPlaceholderOption())
      form.dataset.cityLoadedPrefectureSlug = ''
    }

    const cityOptionsRequestUrl = (prefectureSlug) => {
      const url = new URL(cityOptionsUrl, window.location.origin)
      url.searchParams.set('prefecture', prefectureSlug)
      return url.toString()
    }

    const selectedPrefectureSlug = () => prefectureSelect.selectedOptions[0]?.dataset.prefectureSlug || ''

    let cityOptionsRequestSequence = 0

    const loadCityOptionsForSelectedPrefecture = async () => {
      const requestSequence = ++cityOptionsRequestSequence
      const requestedPrefectureSlug = selectedPrefectureSlug()

      if (!cityOptionsUrl) {
        syncCityOptions()
        return
      }

      if (requestedPrefectureSlug === '') {
        replaceCityOptionsWithPlaceholder()
        syncCityOptions()
        return
      }

      if (form.dataset.cityLoadedPrefectureSlug === requestedPrefectureSlug) {
        syncCityOptions()
        return
      }

      citySelect.disabled = true
      if (cityName) cityName.textContent = '読み込み中...'
      if (cityCount) cityCount.textContent = ''

      try {
        const response = await fetch(cityOptionsRequestUrl(requestedPrefectureSlug))
        if (!response.ok) throw new Error(`Failed to load city options: ${response.status}`)

        const html = await response.text()
        if (requestSequence !== cityOptionsRequestSequence || selectedPrefectureSlug() !== requestedPrefectureSlug) {
          return
        }

        citySelect.innerHTML = html
        form.dataset.cityLoadedPrefectureSlug = requestedPrefectureSlug
        syncCityOptions()
      } catch (error) {
        if (requestSequence !== cityOptionsRequestSequence || selectedPrefectureSlug() !== requestedPrefectureSlug) {
          return
        }

        replaceCityOptionsWithPlaceholder()
        syncCityOptions()
      }
    }

    prefectureSelect.addEventListener('change', () => {
      loadCityOptionsForSelectedPrefecture()
    })
    citySelect.addEventListener('change', syncCityDisplay)
    form.addEventListener('submit', (event) => {
      event.preventDefault()

      const cityUrl = citySelect.disabled ? '' : citySelect.value
      const prefectureUrl = prefectureSelect.value
      const defaultUrl = form.dataset.defaultUrl || ''
      const destinationUrl = cityUrl || prefectureUrl || defaultUrl

      if (destinationUrl) {
        navigate(withRebuildCompanySearchConditions(destinationUrl))
      }
    })

    syncCityOptions()
  })
}

if (typeof document !== 'undefined') {
  const initRebuildCompanyArchiveSearchControls = (root = document) => {
    initRebuildCompanyArchiveAreaSearchToggle(root)
    initRebuildCompanyArchiveSearchForm(root)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRebuildCompanyArchiveSearchControls(), { once: true })
  } else {
    initRebuildCompanyArchiveSearchControls()
  }
}
